"use client"

import { useState, useEffect } from "react"
import StatCard from "./stat-card"
import UserChart from "./charts/user-chart"
import ProjectStatusChart from "./charts/project-status-chart"
import SkillDistributionChart from "./charts/skill-distribution-chart"
import RecentProjects from "./recent-projects"
import { Users, Briefcase, Award, Clock, Calendar } from "lucide-react"
import { motion } from "framer-motion"

export default function DashboardContent() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProjects: 0,
    totalProfessionals: 0,
    pendingProjects: 0,
  })

  const [filters, setFilters] = useState({
    totalUsers: { fromDate: "", toDate: "" },
    totalProjects: { fromDate: "", toDate: "" },
    totalProfessionals: { fromDate: "", toDate: "" },
    pendingProjects: { fromDate: "", toDate: "" },
  })

  const [activeFilter, setActiveFilter] = useState(null)

  const fetchStats = async (key) => {
    const filter = filters[key]
    const query = new URLSearchParams({
      [`${key}From`]: filter.fromDate,
      [`${key}To`]: filter.toDate,
    }).toString()

    const response = await fetch(`http://localhost:5000/dashboard-stats?${query}`)
    const data = await response.json()
    setStats((prev) => ({ ...prev, [key]: data[key] }))
  }

  useEffect(() => {
    fetchStats("totalUsers")
    fetchStats("totalProjects")
    fetchStats("totalProfessionals")
    fetchStats("pendingProjects")
  }, [])

  const handleFilterChange = (key, filterKey, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: { ...prev[key], [filterKey]: value },
    }))
  }

  const renderFilterPopup = (key, title) =>
    activeFilter === key && (
      <div style={{
        position: 'absolute',
        top: '48px',
        right: '0',
        zIndex: 10,
        padding: '20px',
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border-glass)',
        borderRadius: '16px',
        boxShadow: '0 16px 48px rgba(0,0,0,0.3)',
        minWidth: '220px',
      }}>
        <h3 style={{
          marginBottom: '12px',
          fontSize: '0.85rem',
          fontWeight: 600,
          color: 'var(--text-primary)',
          fontFamily: 'var(--font-display)',
        }}>{title} Filter</h3>
        <label style={{ display: 'block', marginBottom: '10px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          From Date:
          <input
            type="date"
            style={{
              display: 'block',
              width: '100%',
              padding: '8px 12px',
              marginTop: '4px',
              background: 'var(--bg-glass)',
              border: '1px solid var(--border-glass)',
              borderRadius: '10px',
              color: 'var(--text-primary)',
              fontSize: '0.8rem',
              outline: 'none',
            }}
            value={filters[key].fromDate}
            onChange={(e) => handleFilterChange(key, "fromDate", e.target.value)}
          />
        </label>
        <label style={{ display: 'block', marginBottom: '12px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          To Date:
          <input
            type="date"
            style={{
              display: 'block',
              width: '100%',
              padding: '8px 12px',
              marginTop: '4px',
              background: 'var(--bg-glass)',
              border: '1px solid var(--border-glass)',
              borderRadius: '10px',
              color: 'var(--text-primary)',
              fontSize: '0.8rem',
              outline: 'none',
            }}
            value={filters[key].toDate}
            onChange={(e) => handleFilterChange(key, "toDate", e.target.value)}
          />
        </label>
        <button
          style={{
            width: '100%',
            padding: '8px 16px',
            borderRadius: '10px',
            border: 'none',
            background: 'linear-gradient(135deg, var(--accent-cyan), var(--accent-violet))',
            color: '#fff',
            fontSize: '0.8rem',
            fontWeight: 600,
            cursor: 'pointer',
          }}
          onClick={() => {
            fetchStats(key)
            setActiveFilter(null)
          }}
        >
          Apply
        </button>
      </div>
    )

  return (
    <main style={{ padding: '32px 24px' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ marginBottom: '40px' }}
      >
        <div style={{
          display: 'inline-block',
          padding: '4px 14px',
          borderRadius: '9999px',
          background: 'rgba(99, 102, 241, 0.1)',
          border: '1px solid rgba(99, 102, 241, 0.2)',
          fontSize: '0.7rem',
          fontWeight: 700,
          color: '#818cf8',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          marginBottom: '12px',
        }}>Admin Panel</div>
        <h1 style={{
          fontFamily: 'var(--font-display)',
          fontSize: '1.8rem',
          fontWeight: 700,
          color: 'var(--text-primary)',
          marginBottom: '6px',
        }}>Dashboard</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Welcome back to your admin dashboard</p>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 lg:grid-cols-4">
        <div style={{ position: 'relative' }}>
          <StatCard
            title="Total Users"
            value={stats.totalUsers}
            icon={<Users style={{ width: '20px', height: '20px', color: '#3b82f6' }} />}
          />
          <Calendar
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              width: '16px',
              height: '16px',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              zIndex: 5,
            }}
            onClick={() => setActiveFilter(activeFilter === "totalUsers" ? null : "totalUsers")}
          />
          {renderFilterPopup("totalUsers", "Total Users")}
        </div>
        <div style={{ position: 'relative' }}>
          <StatCard
            title="Total Projects"
            value={stats.totalProjects}
            icon={<Briefcase style={{ width: '20px', height: '20px', color: '#10b981' }} />}
          />
          <Calendar
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              width: '16px',
              height: '16px',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              zIndex: 5,
            }}
            onClick={() => setActiveFilter(activeFilter === "totalProjects" ? null : "totalProjects")}
          />
          {renderFilterPopup("totalProjects", "Total Projects")}
        </div>
        <div style={{ position: 'relative' }}>
          <StatCard
            title="Professionals"
            value={stats.totalProfessionals}
            icon={<Award style={{ width: '20px', height: '20px', color: '#a78bfa' }} />}
          />
          <Calendar
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              width: '16px',
              height: '16px',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              zIndex: 5,
            }}
            onClick={() => setActiveFilter(activeFilter === "totalProfessionals" ? null : "totalProfessionals")}
          />
          {renderFilterPopup("totalProfessionals", "Professionals")}
        </div>
        <div style={{ position: 'relative' }}>
          <StatCard
            title="Pending Projects"
            value={stats.pendingProjects}
            icon={<Clock style={{ width: '20px', height: '20px', color: '#f59e0b' }} />}
          />
          <Calendar
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              width: '16px',
              height: '16px',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              zIndex: 5,
            }}
            onClick={() => setActiveFilter(activeFilter === "pendingProjects" ? null : "pendingProjects")}
          />
          {renderFilterPopup("pendingProjects", "Pending Projects")}
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-8 mb-8 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={{
            padding: '28px',
            background: 'var(--bg-card)',
            border: '1px solid var(--border-glass)',
            borderRadius: '20px',
          }}
        >
          <h2 style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1.05rem',
            fontWeight: 600,
            color: 'var(--text-primary)',
            marginBottom: '20px',
          }}>User Distribution</h2>
          <div className="h-80">
            <UserChart />
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          style={{
            padding: '28px',
            background: 'var(--bg-card)',
            border: '1px solid var(--border-glass)',
            borderRadius: '20px',
          }}
        >
          <h2 style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1.05rem',
            fontWeight: 600,
            color: 'var(--text-primary)',
            marginBottom: '20px',
          }}>Project Status</h2>
          <div className="h-80">
            <ProjectStatusChart />
          </div>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 gap-8 mb-8 lg:grid-cols-3">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="col-span-2"
          style={{
            padding: '28px',
            background: 'var(--bg-card)',
            border: '1px solid var(--border-glass)',
            borderRadius: '20px',
          }}
        >
          <h2 style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1.05rem',
            fontWeight: 600,
            color: 'var(--text-primary)',
            marginBottom: '20px',
          }}>Top Skills Distribution</h2>
          <div className="h-80">
            <SkillDistributionChart />
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          style={{
            padding: '28px',
            background: 'var(--bg-card)',
            border: '1px solid var(--border-glass)',
            borderRadius: '20px',
          }}
        >
          <h2 style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1.05rem',
            fontWeight: 600,
            color: 'var(--text-primary)',
            marginBottom: '20px',
          }}>Recent Projects</h2>
          <RecentProjects />
        </motion.div>
      </div>
    </main>
  )
}
